import {OrderItemStatus, OrderItems} from '../../../batchDetails/types';
import React, {useCallback, useState} from 'react';

import AppModal from '../../../common/components/CustomModal/AppModal';
import BatchOrderItem from '../BatchOrderItem';
import {CommonScreens} from '@buncha/common/navigation/types';
import {CustomFlatList} from '../../../components/core/list';
import {Divider} from 'native-base';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {MainScreens} from '@buncha/navigation/types';
import MinusTickIcon from '../../../components/core/icon/MinusTickIcon';
import {Modules} from '@buncha/utils/types';
import RightTickIcon from '../../../components/core/icon/RightTickIcon';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {SpinnerComponent} from '../../../components/core/loader';
import {Touchable} from '../../../components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {styles} from './styles';
import {useColorThemeType} from '../../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';

interface BatchOrderItemListProps {
  orderItems: OrderItems[];
  onClick: (orderItem: OrderItems) => void;
  disableOnclick?: boolean;
  esIndex: string;
  runId: number | undefined;
  storeId: number | undefined;
  isSecondShopper: boolean;
}

function DoneOrderItemList(props: BatchOrderItemListProps) {
  const {orderItems, onClick, isSecondShopper} = props;
  const theme = useColorThemeType();
  const [itemId, setItemId] = useState(0);
  const [dropItem, setDropItem] = useState<OrderItems>();
  const [showModal, setShowModal] = useState(false);
  const {title, body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const dropItemModal = useCallback((itemClicked: OrderItems) => {
    setDropItem(itemClicked);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const setStatus = useCallback(
    (orderItem: OrderItems) => {
      setItemId(orderItem.id);
      onClick(orderItem);
      if (orderItem.itemStatus === OrderItemStatus.dropped) closeModal();
    },
    [closeModal, onClick],
  );

  const onItemClick = useCallback(
    (orderItem: OrderItems) => {
      if (orderItem?.isAdjacentOOSItem) return;
      if (orderItem.itemStatus === OrderItemStatus.dropped)
        return dropItemModal(orderItem);
      setStatus(orderItem);
    },
    [dropItemModal, setStatus],
  );

  const getRightIcon = useCallback(
    (item: OrderItems) => {
      if (itemId === item.id) return <SpinnerComponent size={'small'} />;
      switch (item.itemStatus) {
        case OrderItemStatus.dropped:
          return (
            <MinusTickIcon
              width={24}
              height={24}
              viewBox="-3 -1 25 25"
              fill={theme?.background.red[100]}
            />
          );
        case OrderItemStatus.picked:
          if (item.substitutedBy)
            return (
              <RightTickIcon
                width={24}
                height={24}
                viewBox="-3 -1 25 25"
                fill={theme?.background.yellow[100]}
              />
            );
          return (
            <RightTickIcon
              width={24}
              height={24}
              viewBox="-3 -1 25 25"
              fill={theme?.background.green[100]}
            />
          );
        default:
          return (
            <RightTickIcon
              width={24}
              height={24}
              viewBox="-3 -1 25 25"
              fill={theme?.background.green[100]}
            />
          );
      }
    },
    [
      itemId,
      theme?.background.green,
      theme?.background.red,
      theme?.background.yellow,
    ],
  );

  const navigateToItemDetail = useCallback(
    (item: OrderItems) => {
      if (item.isAdjacentOOSItem && !isSecondShopper) return;
      if (item?.catalogItemId && props.esIndex && props?.runId)
        navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.ReplacementItemDetailsPage,
          params: {
            catalogItemId: item?.catalogItemId,
            storeIndex: props.esIndex,
            runId: props?.runId,
            quantity: item.quantity,
            onlyView: true,
          },
        });
      else if (item?.isCustomItem && item?.itemImageUrl)
        navigation.navigate(MainScreens.Common, {
          screen: CommonScreens.ImagePreview,
          params: {
            imageUrl: item?.itemImageUrl,
          },
        });
    },
    [props.esIndex, props.runId],
  );

  const renderItem = useCallback(
    ({item}: {item: OrderItems; index: number}) => {
      return (
        <BatchOrderItem
          runId={props?.runId}
          disabledOpacity={item?.isAdjacentOOSItem}
          esIndex={props.esIndex}
          orderItem={item}
          onClick={navigateToItemDetail}
          storeId={props?.storeId}
          rightIcon={
            <Touchable
              style={[
                styles.touchableStyle,
                // eslint-disable-next-line react-native/no-inline-styles
                (props?.disableOnclick || item?.isAdjacentOOSItem) && {
                  opacity: 0.5,
                },
              ]}
              disabled={props?.disableOnclick}
              onPress={() => onItemClick(item)}>
              {getRightIcon(item)}
            </Touchable>
          }
        />
      );
    },
    [
      props?.runId,
      props.esIndex,
      props?.storeId,
      props?.disableOnclick,
      navigateToItemDetail,
      getRightIcon,
      onItemClick,
    ],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);
  return (
    <React.Fragment>
      <CustomFlatList
        data={orderItems}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
      />
      <AppModal
        isOpen={showModal}
        onClose={closeModal}
        topCTAHandelr={() => {
          if (dropItem) setStatus(dropItem);
        }}
        title={title?.pickAgain}
        subTitle={body?.pickAgain}
        ctaBottomTitle={button?.cancel}
        ctaTopTitle={button?.unDrop}
        topCTAColor={theme?.background.green[100]}
        storeId={props?.storeId}
      />
    </React.Fragment>
  );
}

export default React.memo(DoneOrderItemList);
